/**
 * Time in business, as a number of years.
 *
 * WHY THIS EXISTS. The lender matcher does `Number(criteria.timeInBusiness)`
 * and then `if (tib && tib < minTIB)`. Client Information stored the STRING
 * 'over-two-years' - the only option its dropdown offered, and it was disabled -
 * so that became NaN, NaN is falsy, and the entire TIB check was skipped. Every
 * lender passed the time-in-business gate no matter how new the business was,
 * which is the opposite of a filter: a business open three months was being
 * recommended lenders that require eighteen.
 *
 * 943 client records carry that string today, so parsing it is not optional -
 * treating it as unknown would swing the same deals from "always passes" to
 * "always fails", which is just as wrong in the other direction.
 *
 * The start date is preferred over the stored label wherever there is one. 144
 * records are labelled over-two-years while their own start date says under
 * two, and the date is the fact - the label is a guess somebody could not edit.
 */

const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

// Legacy stored labels, mapped to the number of years each one asserts. These
// are floors, not midpoints: 'over-two-years' guarantees at least 2, and
// claiming 3 on its behalf would pass a lender minimum the record cannot
// actually support.
const LABELS = {
  'over-two-years': 2,
  'over-2-years': 2,
  'two-years-plus': 2,
  '2-years-plus': 2,
  'one-to-two-years': 1,
  '1-2-years': 1,
  'six-to-twelve-months': 0.5,
  'six-months-to-one-year': 0.5,
  'under-six-months': 0,
  'less-than-six-months': 0,
};

/** Years between a start date and now, or null when the date is unusable. */
export function yearsSince(startDate) {
  if (!startDate) return null;
  const s = String(startDate).trim();

  // MMDDYYYY with no separators appears in this data ("042015" and similar are
  // handled below); a bare 8-digit run is month, day, year.
  let ms = NaN;
  if (/^\d{8}$/.test(s)) {
    ms = Date.parse(`${s.slice(0, 2)}/${s.slice(2, 4)}/${s.slice(4)}`);
  } else if (/^\d{6}$/.test(s)) {
    // MMYYYY, e.g. "042015" - April 2015. Read as the first of that month,
    // which is the earliest the business could have started, so the resulting
    // TIB is never overstated.
    ms = Date.parse(`${s.slice(0, 2)}/01/${s.slice(2)}`);
  } else {
    ms = Date.parse(s);
  }
  if (!Number.isFinite(ms)) return null;

  const years = (Date.now() - ms) / MS_PER_YEAR;
  // A future start date is a typo, not a business that has not begun. Reported
  // as unknown rather than negative, so it cannot quietly pass a `< minTIB`
  // comparison by being less than everything.
  if (years < 0) return null;
  return Math.round(years * 100) / 100;
}

/**
 * The best number of years available for a client or a deal-criteria object.
 *
 * Order: an explicit number, then the start date, then a legacy label. The
 * number comes first because Auto Submit's own "Time in Biz (yrs)" field is
 * numeric and is what a rep typed for THIS deal.
 *
 * @returns {number|null} null means genuinely unknown - callers must decide
 *   what to do with that rather than being handed a 0 that reads as "brand new".
 */
export function tibYears(source = {}) {
  const raw = source.timeInBusiness;

  if (typeof raw === 'number' && Number.isFinite(raw) && raw >= 0) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const s = raw.trim();
    // A plain numeric string ("4", "1.5") is years.
    if (/^\d+(\.\d+)?$/.test(s)) return Number(s);
    const label = LABELS[s.toLowerCase().replace(/\s+/g, '-')];
    if (label !== undefined) {
      // The start date, where there is one, overrides the label - see this
      // file's header on the 144 records whose label contradicts their date.
      const fromDate = yearsSince(source.businessStartDate);
      return fromDate != null ? fromDate : label;
    }
  }
  return yearsSince(source.businessStartDate);
}

/** How it should read on screen. */
export function tibLabel(source = {}) {
  const y = tibYears(source);
  if (y == null) return 'Unknown';
  if (y < 1) return `${Math.round(y * 12)} months`;
  return `${y.toFixed(1)} years`;
}

/**
 * Whether a lender's minimum is met.
 *
 * Unknown TIB returns `true` - it does not fail the lender - but the caller is
 * told it was unchecked so it can say so, rather than presenting an unverified
 * pass as a verified one. Failing on unknown would drop every lender for the
 * ~2,000 records that have neither a date nor a usable label.
 *
 * @returns {{ok: boolean, years: number|null, checked: boolean}}
 */
export function meetsMinTib(source, minTIB) {
  const years = tibYears(source);
  const min = Number(minTIB) || 0;
  if (years == null) return { ok: true, years: null, checked: false };
  return { ok: years >= min, years, checked: true };
}

export default { tibYears, tibLabel, yearsSince, meetsMinTib };
